import * as Sentry from '@sentry/react'
import api from '../api/client'

/**
 * Delivery of client-completed conveyor resolutions (ADR-086).
 *
 * A resolution the client completes on its own — today only an accepted typed
 * retype — is reported to the server **by the interaction that created it**
 * (Decision 1), never deferred to a later, unrelated call. The deferred
 * mechanism this replaces lost the resolution in four ways, all documented in
 * `docs/diagnostics/daily-goal-40-shows-39-terminal-retype-2026-08-24.md`:
 * the session's last card had no next call to ride (L1), a soft reject returned
 * before the booking sites and ate the in-flight flag (L2), any failed `/answer`
 * destroyed it because the flag was consumed at mutate time (L3), and a reload
 * in the window dropped it (L4).
 *
 * What this module holds is the part that survives delivery-with-the-event: the
 * single in-flight request can still fail, and Decision 3 says that must never
 * be silent.
 *
 * - A report is **outstanding until acknowledged**, not until a request was
 *   created (3a — the precise inversion of L3's mechanism).
 * - An outstanding report **survives the failure of its call** and is offered
 *   again on the next session-scoped call, up to and including completion (3b).
 * - A report that can **never** be delivered leaves an observable record (3c).
 *   A transient failure that will be re-offered is not yet undeliverable, so it
 *   is a breadcrumb; a definitive refusal or an abandoned report is reported.
 * - The learner's displayed counter is **never** reconciled downward from here
 *   (3d) — nothing in this module returns a count, deliberately.
 *
 * At-most-once (Decision 4) is *not* implemented here. It belongs to the report
 * itself: each carries a stable `resolution_id`, and the server's unique
 * constraint makes a repeat delivery inert. That is exactly what Decision 4
 * requires — a guarantee held in a retry helper is not inherited by the next
 * caller, so this helper deliberately does not hold one.
 */

export type ResolutionKind = 'typed_retype'

/**
 * How long session completion waits for outstanding reports before proceeding.
 *
 * Short on purpose. Because expiry now discards nothing — the report stays
 * outstanding and a late acknowledgement still books it (see
 * `waitBeforeCompletion`) — this wait buys only one thing: that in the common
 * case the resolution is already counted when the completion screen reads its
 * numbers. It is not protecting against loss, so there is nothing to trade a
 * longer wait for.
 *
 * 1.5s covers a slow-but-alive mobile round trip several times over, while being
 * short enough that a dead network barely delays the completion screen at all.
 */
const COMPLETION_WAIT_MS = 1_500

export interface ResolutionReport {
  resolutionId: string
  kind: ResolutionKind
  cardId: string
  /**
   * The session this resolution happened in, captured when the report is
   * created rather than read at delivery time.
   *
   * This is load-bearing. A report can outlive the session that produced it —
   * a failed delivery stays outstanding, and the learner can start a new
   * session before it is re-offered. Resolving the session id late would credit
   * one session's resolution to another, which is the ADR-082 Decision 1
   * violation (a resolution belongs to the day, and the session, it happened
   * in) dressed up as a retry.
   */
  sessionId: string
}

/** Outcome of one delivery attempt. */
type DeliveryOutcome =
  /** The server booked it, or acknowledged it as already booked. Settled. */
  | 'acknowledged'
  /** The server refused definitively (day boundary, closed session). Settled. */
  | 'refused'
  /** Transient — the report stays outstanding and is offered again. */
  | 'retryable'

function errorCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { detail?: { code?: string } } } })
    ?.response?.data?.detail?.code
}

function errorStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

/**
 * A refusal the server has already decided and will decide the same way again.
 *
 * 409 is the whole set today: `SESSION_DAY_ENDED` (ADR-082 — the session began
 * before the person's day boundary) and a plain not-active session. Decision 6
 * makes the boundary refusal **definitive**: it is not retried and never
 * re-offered against the new day's session, because that would re-attribute one
 * day's resolution to another day, which ADR-082 Decision 1 forbids outright.
 *
 * 4xx other than 409 is also treated as settled — a request the server rejects
 * as malformed will not become well-formed by being sent again.
 */
function isDefinitiveRefusal(err: unknown): boolean {
  const status = errorStatus(err)
  if (status === undefined) return false
  return status >= 400 && status < 500
}

/**
 * Tracks outstanding reports for one session and delivers them.
 *
 * In-memory by design: a report is sent with the event that created it, so the
 * window in which one can be outstanding is an in-flight request rather than
 * "until the learner's next answer", which is what made the old flag worth
 * persisting and still impossible to persist correctly.
 *
 * A report leaves this reporter in exactly two ways: acknowledged by the server,
 * or definitively refused by it. There is no third exit — in particular, no
 * amount of elapsed time removes one. Each stays bound to the session that
 * created it, so a report that outlives its session is still delivered to that
 * session and never re-attributed to a later one.
 *
 * The residual is stated rather than hidden: if the page is destroyed while a
 * report is outstanding, nothing can observe it — no code runs to report the
 * loss. Every other path through this module is observable.
 */
export class ResolutionReporter {
  private outstanding = new Map<string, ResolutionReport>()
  private inFlight = new Map<string, Promise<void>>()

  /** True while any report has not been acknowledged or definitively refused. */
  get hasOutstanding(): boolean {
    return this.outstanding.size > 0
  }

  /**
   * Report a resolution the client just completed. Fire-and-forget by design:
   * the caller must not await this. ADR-086 Decision 2 forbids putting a network
   * round trip between the learner's keystroke and the card's acceptance, so the
   * acceptance has already happened on screen before this is called.
   */
  report(report: ResolutionReport): void {
    this.outstanding.set(report.resolutionId, report)
    void this.deliver(report)
  }

  /**
   * Offer every outstanding report again and resolve once all are settled.
   *
   * Awaited before session completion (Decision 5 — completion never outruns the
   * counter) and fired without awaiting alongside an answer submission (3b —
   * re-offered on the next session-scoped call, without adding latency to the
   * learner's path).
   */
  async flush(): Promise<void> {
    const pending = [...this.outstanding.values()].map((r) => this.deliver(r))
    await Promise.all(pending)
  }

  /**
   * Give outstanding reports a brief chance to land before session completion is
   * requested, then let completion proceed regardless.
   *
   * **Expiry stops the waiting, and nothing else.** A report still outstanding
   * when the deadline passes is *not* marked undeliverable and *not* discarded:
   * it stays outstanding, stays bound to its own session, and its in-flight
   * attempt keeps running — so a late acknowledgement still books it. Elapsed
   * time is never by itself evidence that delivery is impossible. Treating it as
   * such would convert a slow network into a permanently lost resolution, which
   * is precisely the failure class this whole change exists to remove.
   *
   * A report becomes undeliverable only when delivery is genuinely impossible: a
   * definitive server rejection, or the day-boundary refusal Decision 6 makes
   * final. Both are recorded where they happen, in `post`.
   *
   * The wait exists so the common case is already booked by the time the
   * completion screen reads its numbers — not to protect against loss, since
   * expiry now loses nothing. That is why it can be short.
   *
   * Always resolves.
   */
  async waitBeforeCompletion(): Promise<void> {
    await Promise.race([
      this.flush(),
      new Promise<void>((resolve) => setTimeout(resolve, COMPLETION_WAIT_MS)),
    ])
  }

  private async deliver(report: ResolutionReport): Promise<void> {
    // Collapse concurrent attempts on the same report so a flush racing the
    // initial send does not open a second request. This is a politeness, not a
    // correctness mechanism — correctness is the server's unique constraint.
    const existing = this.inFlight.get(report.resolutionId)
    if (existing) return existing

    const attempt = this.attempt(report).finally(() => {
      this.inFlight.delete(report.resolutionId)
    })
    this.inFlight.set(report.resolutionId, attempt)
    return attempt
  }

  private async attempt(report: ResolutionReport): Promise<void> {
    const outcome = await this.post(report.sessionId, report)
    if (outcome === 'retryable') return // stays outstanding, offered again later

    // Acknowledged or definitively refused — settled either way.
    this.outstanding.delete(report.resolutionId)
  }

  private async post(
    sessionId: string,
    report: ResolutionReport,
  ): Promise<DeliveryOutcome> {
    try {
      await api.post(`/api/v1/learning/sessions/${sessionId}/resolutions`, {
        resolution_id: report.resolutionId,
        kind: report.kind,
        card_id: report.cardId,
      })
      return 'acknowledged'
    } catch (err) {
      if (isDefinitiveRefusal(err)) {
        this.observeUndeliverable(
          report,
          `refused by server (status ${errorStatus(err)}, code ${errorCode(err) ?? 'none'})`,
        )
        return 'refused'
      }
      // Transient: stays outstanding. Not reported as a loss yet — it is not one
      // until it can never be delivered (Decision 3c).
      Sentry.addBreadcrumb({
        category: 'resolution-report',
        level: 'warning',
        message: 'resolution report delivery failed; still outstanding',
        data: { resolutionId: report.resolutionId, kind: report.kind },
      })
      return 'retryable'
    }
  }

  /**
   * Decision 3(c): a report that can never be delivered is never dropped
   * quietly. A counting defect nobody can see is the failure this whole record
   * exists to close, and re-creating it in a smaller window would be the same
   * mistake at a smaller scale.
   */
  private observeUndeliverable(report: ResolutionReport, reason: string): void {
    Sentry.captureMessage(
      `Conveyor resolution report undeliverable: ${reason}`,
      {
        level: 'error',
        tags: { adr: 'ADR-086', resolution_kind: report.kind },
        extra: {
          resolutionId: report.resolutionId,
          cardId: report.cardId,
          reason,
        },
      },
    )
  }
}
